import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { AdminPages, buildPageId, buildRootPageTarget } from '../../AdminLinkUtils';
import InfrastructureDetailBase from '../../infrastructure/InfrastructureDetailBase';
import { NAME_FIELD, NEW_BREAD_CRUMB_TEXT } from '../../infrastructure/InfrastructureConstants';
import { useInfrastructureErrorHandler } from '../../infrastructure/hooks/UseInfrastructureErrorHandler';
import {
    BREADCRUMB_TEXT_SUFFIX,
    getTestTypeDisplayTitle,
    SERVICE_TESTS_DETAIL_ACTION_URLS,
    SERVICE_TESTS_DETAIL_FUNCTION_NAME,
    TEST_TYPE_FIELD,
    TYPE_VOIP,
    TYPE_WEB,
    WIFI_TEST_TYPES,
} from '../ServiceTestsConstants';
import { buildCopyNavigationPath, duplicateTestData } from '../DuplicateTestUtils';
import { useDataService } from '../../../../services/ApiDataService';
import { useUserId, useUserName } from '../../../../stores/SessionStore';
import { useNavigate } from '../../../../utilities/PgmUtilities';
import TestDetailActionButtons from './TestDetailActionButtons';
import {
    BASE_URL,
    DEFAULT_VALUES,
    filterId,
    getTestTypeTitle,
    GOTO_PARENT_TYPE_URL,
    GRANDPARENT_FUNCTION_BREADCRUMBS,
    target,
    VOIP_DEFAULT_VALUES,
    WEB_DEFAULT_VALUES,
} from './TestDetailDefaults';
import TestDetailBottomButtons from './TestDetailBottomButtons';
import {
    MemoBaseInformation,
    MemoBttCertificate,
    MemoDynamicConfig,
    MemoExpectedResponse,
    MemoNotifications,
    MemoPathAnalysis,
    MemoRequestParams,
    MemoRunOnnPoints,
    MemoRunSchedule,
    MemoSmartEdge,
    MemoThresholds,
    MemoVoipPhone,
    MemoVoipPulse,
} from './TestDetailMemoSections';
import {
    TestTypeContextProvider,
    useSubmitTransforms,
    useTestTypeArrayContext,
    useVerifyLifecycle,
} from './hooks/TestDetailHooks';
import { VOIP_PHONE_TEST_TYPE, VOIP_PULSE_TEST_TYPE } from '../../../panels/voip-test-now-panels/VoipTestConstants';
import TestVerifyResultSection from './sections/TestVerifyResultSection';
import { useWifiTests } from './sections/dynamic-config/UseDynamicConfigData';
import { AUTH_NONE } from '../../../panels/web-test-now-panels/WebTestConstants';
import TestEmptyRunScheduleSection from './sections/TestEmptyRunScheduleSection';

const RETURN_TO_PARAM = 'returnTo';
const RETURN_TO_ERROR_DETAILS = 'testTypes.errorDetails';
const ERROR_LIST_PAGE_STATE = 'tests-errors-list';

function getReturnNavigationUrl(search) {
    const params = new URLSearchParams(search || '');

    if (params.get(RETURN_TO_PARAM) === RETURN_TO_ERROR_DETAILS) {
        return `${buildRootPageTarget(AdminPages.ServiceTests)}?pageState=${ERROR_LIST_PAGE_STATE}`;
    }

    return GOTO_PARENT_TYPE_URL;
}

export default function TestDetailPanel({ testId, dupOfId, initialTestTypeId, groupExist = true }) {
    const [testTypeId, setTestTypeId] = useState(groupExist ? initialTestTypeId ?? null : null);
    const { search } = useLocation();
    const testType = useTestTypeArrayContext(testTypeId, groupExist);
    const bttStateRef = useRef({ certAuthEnabled: false, runAsUser: false, defaultLogin: true });
    const wifiTestListRef = useRef([]);
    const matchingAgentsRef = useRef([]);
    const thresholdsRef = useRef([]);
    // authTypeRef: tracks the currently selected auth type in TestAuthenticationSection so
    // testConfigTransform can correctly discard stale auth types after deepMergeForSave.
    const authTypeRef = useRef(AUTH_NONE);
    const group = testType?.group;
    const typeName = testType?.name;
    const typeId = testType?._id;
    const typeTitle = getTestTypeTitle(testType, group);

    const recordId = testId;

    const dataTableActionErrorHandler = useInfrastructureErrorHandler();
    const navigate = useNavigate();

    const verifyResultSectionRef = useRef(undefined);

    const [copyDefaults, setCopyDefaults] = useState(undefined);
    const [copyLoading, setCopyLoading] = useState(!!dupOfId);
    const goToUrl = useMemo(() => getReturnNavigationUrl(search), [search]);

    const dataService = useDataService();

    const [userName] = useUserName();
    const [userId] = useUserId();

    // matchingAgentsRef: 1-level prop drilling to TestRunOnnPointsSection → ServiceTestAgentSelector
    // is intentional (ARCH-01). The ref is also consumed by useVerifyLifecycle and useSubmitTransforms
    // (wifi transform), so it cannot be fully encapsulated in a child component.
    // Context would add complexity without benefit.

    const { submitTransform, createSubmitTransform } = useSubmitTransforms({
        testType,
        typeId,
        dataService,
        bttStateRef,
        wifiTestListRef,
        matchingAgentsRef,
        thresholdsRef,
        authTypeRef,
    });

    const { showVerify, verifyLoading, verifyResult, onVerify } = useVerifyLifecycle({
        testType,
        matchingAgentsRef,
        userName,
        userId,
        dataService,
        recordId,
        verifyResultSectionRef,
    });

    useEffect(() => {
        if (!dupOfId) {
            return;
        }
        setCopyLoading(true);
        dataService
            .get(`/ngp/v2/service-tests/${dupOfId}`)
            .then((sourceData) => {
                setCopyDefaults(duplicateTestData(sourceData, dupOfId));
            })
            .finally(() => {
                setCopyLoading(false);
            });
    }, [dataService, dupOfId]);

    const functionBreadCrumbs = useMemo(() => {
        if (!groupExist) {
            return [];
        }
        const parentTitle = typeTitle + BREADCRUMB_TEXT_SUFFIX;
        const parentFunctionBreadcrumbs = { text: parentTitle, link: GOTO_PARENT_TYPE_URL };
        return [GRANDPARENT_FUNCTION_BREADCRUMBS, parentFunctionBreadcrumbs];
    }, [groupExist, typeTitle]);

    // Type-specific Create-mode defaults: VoIP needs call settings, Web needs
    // method + responseCode. All types get scheduling: {} so UseScheduleFormBridge
    // can initialize the schedule radio to Interval (portal/ui default).
    const computedDefaultValues = useMemo(() => {
        if (group === TYPE_VOIP) {
            return VOIP_DEFAULT_VALUES;
        }
        if (group === TYPE_WEB) {
            return WEB_DEFAULT_VALUES;
        }
        return DEFAULT_VALUES;
    }, [group]);

    // portal/ui breadcrumb: "Create a new {testTypeTitle} Test" (config.tests.new i18n key).
    // Copy mode uses the source test's name (already prefixed with "Copy of").
    const createBreadcrumbText = useMemo(() => {
        if (!groupExist) {
            return '';
        }
        if (copyDefaults?.name) {
            return copyDefaults.name;
        }
        const displayTitle = getTestTypeDisplayTitle(testType);
        if (!displayTitle) {
            return NEW_BREAD_CRUMB_TEXT;
        }
        return i18n._(t`Create a new ${displayTitle} Test`);
    }, [copyDefaults?.name, groupExist, testType]);

    const handleCopyAction = useCallback(
        (event) => {
            event.currentTarget.blur();
            event.preventDefault();
            event.stopPropagation();

            navigate(buildCopyNavigationPath(recordId, { fromDetail: true }));
        },
        [navigate, recordId]
    );

    const renderActionButtons = useCallback(
        (actionProps) => {
            return (
                <TestDetailActionButtons
                    {...actionProps}
                    recordId={recordId}
                    createSubmitTransform={createSubmitTransform}
                    submitTransform={submitTransform}
                    handleCopyAction={handleCopyAction}
                    onVerify={onVerify}
                    verifyLoading={verifyLoading}
                    bttStateRef={bttStateRef}
                    groupExist={groupExist}
                />
            );
        },
        [recordId, createSubmitTransform, submitTransform, handleCopyAction, onVerify, verifyLoading, groupExist]
    );

    const getTargetConfig = useCallback(() => {
        return {
            target,
            mappedFilterParams: filterId
                ? {
                      [filterId]: recordId,
                  }
                : {},
        };
    }, [recordId]);

    const isWebTestConfig = testType?.testBuilder?.details?.directive === 'web-test-configuration';
    const hasThresholds = !isEmpty(testType?.thresholds);
    const hasDynamicSections =
        !isEmpty(testType?.testBuilder?.details?.sections) && !testType?.testBuilder?.details?.directive;
    const showBttCertSection = testType?.bttTest === true || testType?.showBttAuth === true;

    const wifiSections = useMemo(() => testType?.testBuilder?.details?.sections || [], [testType]);
    const { wifiTestList } = useWifiTests(wifiSections);

    // Keep refs current for use inside transform callbacks (avoids stale closures)
    useEffect(() => {
        wifiTestListRef.current = wifiTestList;
    }, [wifiTestList]);

    const renderFormContent = useCallback(
        ({ isCreateAction, formData, handleSubmit, handleSubmitFunction, saveDisabled, isActioning }) => {
            const showBeaconSection = !WIFI_TEST_TYPES.has(typeName);
            // Determine if the test type supports trace route.
            const canRunTraceRoute = group === TYPE_WEB;

            if (!groupExist) {
                return (
                    <>
                        <MemoBaseInformation />
                        <TestEmptyRunScheduleSection />
                        {!isCreateAction && <MemoNotifications groupExist={groupExist} />}
                        <TestDetailBottomButtons
                            formData={formData}
                            handleSubmit={handleSubmit}
                            handleSubmitFunction={handleSubmitFunction}
                            submitTransform={submitTransform}
                            saveDisabled={saveDisabled}
                            isActioning={isActioning}
                            onVerify={onVerify}
                            verifyLoading={verifyLoading}
                            bttStateRef={bttStateRef}
                            groupExist={groupExist}
                        />
                    </>
                );
            }

            return (
                <>
                    <MemoBaseInformation />
                    <MemoRunSchedule />
                    {canRunTraceRoute && <MemoPathAnalysis />}
                    {typeName !== VOIP_PULSE_TEST_TYPE && <MemoRunOnnPoints matchingAgentsRef={matchingAgentsRef} />}
                    {typeName === VOIP_PULSE_TEST_TYPE && <MemoVoipPulse />}
                    {typeName === VOIP_PHONE_TEST_TYPE && <MemoVoipPhone />}
                    {isWebTestConfig && <MemoRequestParams authTypeRef={authTypeRef} />}
                    {isWebTestConfig && <MemoExpectedResponse />}
                    {hasDynamicSections && <MemoDynamicConfig testId={testId} />}
                    {showBttCertSection && <MemoBttCertificate bttStateRef={bttStateRef} />}
                    {showBeaconSection && <MemoSmartEdge />}
                    {hasThresholds && <MemoThresholds thresholdsRef={thresholdsRef} />}
                    {!isCreateAction && <MemoNotifications groupExist={groupExist} />}
                    <TestVerifyResultSection
                        showVerify={showVerify}
                        verifyLoading={verifyLoading}
                        verifyResult={verifyResult}
                        verifyResultSectionRef={verifyResultSectionRef}
                    />
                    <TestDetailBottomButtons
                        formData={formData}
                        handleSubmit={handleSubmit}
                        handleSubmitFunction={handleSubmitFunction}
                        submitTransform={submitTransform}
                        saveDisabled={saveDisabled}
                        isActioning={isActioning}
                        onVerify={onVerify}
                        verifyLoading={verifyLoading}
                        bttStateRef={bttStateRef}
                        groupExist={groupExist}
                    />
                </>
            );
        },
        [
            testId,
            groupExist,
            isWebTestConfig,
            hasThresholds,
            hasDynamicSections,
            showBttCertSection,
            typeName,
            group,
            showVerify,
            verifyLoading,
            verifyResult,
            onVerify,
            submitTransform,
        ]
    );

    if (copyLoading) {
        return null;
    }

    return (
        <TestTypeContextProvider testType={testType}>
            <InfrastructureDetailBase
                recordId={recordId}
                showSaveButton={false}
                showBreadcrumb={groupExist}
                showSave={false}
                tableSelectionAction
                baseUrl={BASE_URL}
                goToUrl={goToUrl}
                defaultValues={copyDefaults || computedDefaultValues}
                pageId={buildPageId(SERVICE_TESTS_DETAIL_FUNCTION_NAME)}
                nameField={NAME_FIELD}
                typeField={TEST_TYPE_FIELD}
                newRecordBreadcrumbText={createBreadcrumbText}
                functionBreadcrumbs={functionBreadCrumbs}
                actionUrls={SERVICE_TESTS_DETAIL_ACTION_URLS}
                getTargetConfig={getTargetConfig}
                actionButtonsFn={renderActionButtons}
                errorHandler={dataTableActionErrorHandler}
                submitTransform={submitTransform}
                createSubmitTransform={createSubmitTransform}
                setTestTypeId={setTestTypeId}
            >
                {renderFormContent}
            </InfrastructureDetailBase>
        </TestTypeContextProvider>
    );
}

TestDetailPanel.propTypes = {
    testId: PropTypes.string,
    dupOfId: PropTypes.string,
    initialTestTypeId: PropTypes.string,
    groupExist: PropTypes.bool,
};

TestDetailPanel.defaultProps = {
    testId: undefined,
    dupOfId: undefined,
    initialTestTypeId: undefined,
    groupExist: true,
};
